import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, CheckSquare, Ban, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTransformationOrders } from '@/hooks/useTransformationOrders';
import { useTransformationRules } from '@/hooks/useTransformationRules';
import { useIngredients } from '@/pages/inventory/hooks/useIngredients';
import { useAuth } from '@/context/AuthContext';
import { useAccessibleLocations } from '@/hooks/useAccessibleLocations';
import { TransformationActionModal } from './components/TransformationActionModal';
import { TransformationStatesLegend } from './components/TransformationStatesLegend';
import { TransformationOrdersTable } from './components/TransformationOrdersTable';
import { transformationOrdersService } from '@/services/transformationOrders.service';
import { toast } from 'sonner';
import type { TransformationFilters } from '@/types/transformation.types';

// Calcular rango lunes-domingo de la semana actual
const getWeekRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=domingo, 1=lunes...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(monday), dateTo: fmt(sunday) };
};

// Calcular ayer y hoy para filtros de cajero
const getYesterdayTodayRange = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const fmt = (d: Date) => d.toISOString().split('T')[0];
  return { dateFrom: fmt(yesterday), dateTo: fmt(today) };
};

export const TransformationOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, selectedLocation } = useAuth();
  const { locationsWithAll: locations } = useAccessibleLocations();
  const weekRange = getWeekRange();

  // Verificar si el usuario es cajero (positionId = 3)
  const isCashier = user?.positionId === 3;
  const cajeroRange = getYesterdayTodayRange();

  // Para cajero: filtrar por su local y ayer/hoy
  const initialDateFrom = isCashier ? cajeroRange.dateFrom : weekRange.dateFrom;
  const initialDateTo = isCashier ? cajeroRange.dateTo : weekRange.dateTo;
  const initialLocationId = isCashier && selectedLocation?.id ? selectedLocation.id.toString() : 'all';

  const [filters, setFilters] = useState<Partial<TransformationFilters>>({
    locationId: initialLocationId,
    status: 'all',
    sourceIngredientId: 'all',
    targetIngredientId: 'all',
    dateFrom: initialDateFrom,
    dateTo: initialDateTo
  });
  const { orders, isLoading, filterOrders, resetFilters } = useTransformationOrders({
    locationId: initialLocationId !== 'all' ? initialLocationId : undefined,
    dateFrom: initialDateFrom,
    dateTo: initialDateTo
  });
  const { rules } = useTransformationRules();
  const { ingredients } = useIngredients();

  // Helper function to find ingredient by ID
  const getIngredientById = (ingredientId: number | string) => {
    return ingredients.find(ing => ing.id.toString() === ingredientId.toString());
  };
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [actionModal, setActionModal] = useState<{
    isOpen: boolean;
    type: 'complete' | 'cancel' | null;
    orderId: string;
    orderNumber: string;
  }>({
    isOpen: false,
    type: null,
    orderId: '',
    orderNumber: ''
  });

  const handleFilterChange = (key: keyof TransformationFilters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    handleFilterChange('status', status);
    filterOrders({ ...filters, status });
  };

  // Status filter options
  const statusFilters: Array<{ key: string, label: string, activeBg: string, borderColor: string, countColor: string }> = [
    { key: 'all', label: 'Todas', activeBg: 'bg-gray-700 text-white', borderColor: 'border-l-gray-400', countColor: 'text-gray-500' },
    { key: 'pending', label: 'En Proceso', activeBg: 'bg-amber-500 text-white', borderColor: 'border-l-amber-500', countColor: 'text-amber-600' },
    { key: 'completed', label: 'Completadas', activeBg: 'bg-emerald-500 text-white', borderColor: 'border-l-emerald-500', countColor: 'text-emerald-600' },
    { key: 'cancelled', label: 'Canceladas', activeBg: 'bg-rose-500 text-white', borderColor: 'border-l-rose-500', countColor: 'text-rose-600' }
  ];

  // Get count for each status
  const getStatusCount = (status: string) => {
    if (status === 'all') return orders.length;
    return orders.filter(order => order.status === status).length;
  };

  const handleSearch = () => {
    filterOrders(filters);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Open action modal
  const openActionModal = (type: 'complete' | 'cancel', orderId: string, orderNumber: string) => {
    setActionModal({
      isOpen: true,
      type,
      orderId,
      orderNumber
    });
  };

  // Close action modal
  const closeActionModal = () => {
    setActionModal({
      isOpen: false,
      type: null,
      orderId: '',
      orderNumber: ''
    });
  };

  // Action handlers for transformation workflow
  const handleDeleteOrder = async (orderId: string) => {
    try {
      await transformationOrdersService.cancelTransformation(orderId, { reason: 'Eliminada por usuario' });
      toast.success('Orden eliminada exitosamente');
      resetFilters();
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Error al eliminar la orden');
    }
  };

  const executeAction = async (data?: any) => {
    const { orderId, type } = actionModal;

    try {
      switch (type) {
        case 'complete':
          if (!data?.actualTargetQuantity) {
            toast.error('Debe especificar la cantidad real obtenida');
            return;
          }

          await transformationOrdersService.executeTransformation(orderId, {
            actualTargetQuantity: parseFloat(data.actualTargetQuantity),
            notes: data.notes
          });
          toast.success('Transformación completada exitosamente');
          break;

        case 'cancel':
          await transformationOrdersService.cancelTransformation(orderId, {
            reason: data?.reason || 'Eliminada por usuario'
          });
          toast.success('Orden eliminada exitosamente');
          break;
      }

      closeActionModal();
      resetFilters();
    } catch (error) {
      console.error('Error executing action:', error);
      toast.error('Error al ejecutar la acción');
    }
  };

  // Get available actions based on order status and user role
  const getAvailableActions = (order: any) => {
    const actions = [];
    
    // Always available: View details
    actions.push({ type: 'view', icon: Eye, label: 'Ver detalles', variant: 'ghost' });
    
    switch (order.status) {
      case 'pending':
        actions.push({ 
          type: 'complete', 
          icon: CheckSquare, 
          label: 'Completar transformación',
          variant: 'ghost',
          color: 'text-green-600 hover:text-green-800',
          onClick: () => openActionModal('complete', order.id, order.orderNumber)
        });
        actions.push({ 
          type: 'cancel', 
          icon: Ban, 
          label: 'Cancelar orden',
          variant: 'ghost',
          color: 'text-red-600 hover:text-red-800',
          onClick: () => openActionModal('cancel', order.id, order.orderNumber)
        });
        break;
        
      case 'completed':
      case 'cancelled':
        // No actions available for completed/deleted orders
        break;
    }
    
    return actions;
  };


  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Órdenes de Transformación</h1>
            <p className="text-gray-600">Gestión de transformaciones de insumos</p>
          </div>
        </div>
        <Card className="animate-pulse">
          <CardContent className="p-4">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Órdenes de Transformación</h1>
          <p className="text-gray-600">Gestión de transformaciones de insumos</p>
        </div>
        <div className="flex gap-2">
          {!isCashier && <TransformationStatesLegend />}
          <Button size="sm" onClick={() => navigate('/transformations/create')}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Transformación
          </Button>
        </div>
      </div>

      {/* Filters - Ocultos para cajeros */}
      {!isCashier && (
        <Card>
          <CardContent className="pt-4 pb-3 space-y-2">
            {/* Fila 1: Filtros de búsqueda */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Desde:</label>
                <Input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Hasta:</label>
                <Input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Local:</label>
                <Select
                  value={filters.locationId || 'all'}
                  onValueChange={(value) => handleFilterChange('locationId', value)}
                >
                  <SelectTrigger className="h-9 w-48 text-sm">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id.toString()}>
                        {loc.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                className="h-9 bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={handleSearch}
              >
                <Search className="mr-1 h-4 w-4" />
                Buscar
              </Button>
            </div>
            {/* Fila 2: Chips de estado */}
            <div className="flex flex-wrap items-center gap-1.5">
              {statusFilters.map((sf) => {
                const count = getStatusCount(sf.key);
                const isActive = selectedStatus === sf.key;
                return (
                  <button
                    key={sf.key}
                    onClick={() => handleStatusFilter(sf.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? sf.activeBg
                        : `bg-white border border-gray-200 border-l-[3px] ${sf.borderColor} text-gray-700 hover:bg-gray-50`
                    }`}
                  >
                    {sf.label}
                    <span className={`text-xs font-bold ${isActive ? 'text-white' : sf.countColor}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders Table */}
      <TransformationOrdersTable
        orders={orders}
        rules={rules}
        getIngredientById={getIngredientById}
        formatDate={formatDate}
        getAvailableActions={getAvailableActions}
      />


      {/* Action Modal */}
      <TransformationActionModal
        isOpen={actionModal.isOpen}
        type={actionModal.type}
        orderNumber={actionModal.orderNumber}
        onClose={closeActionModal}
        onConfirm={executeAction}
      />
    </div>
  );
};